import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";

import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useIssueTypePool } from "#app/components/entity-selectors/hooks";
import type { Omittable } from "@salestrak/ui";

type IssueTypeInputProps = Omittable<{
  value: unknown;
  onChange: (value: unknown) => void;
  icon?: IconName;
  /** Stretch the trigger to fill its container. */
  fill?: boolean;
}>;

/**
 * Issue type selector for filter panels.
 * Reads issue types from workspace data (already cached).
 */
export function IssueTypeInput({ value, onChange, icon, fill }: IssueTypeInputProps) {
  const { issueTypes } = useWorkspaceData();
  const { pool } = useIssueTypePool({ issueTypes });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 0,
      searchPlaceholder: "Search project types...",
      emptyMessage: "No matching project types",
      noItemsMessage: "No project types yet",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    // Narrowed inside the memo: a `[]` fallback built outside it is a new
    // array every render, and the memo would never hold.
    const selectedIds = Array.isArray(value) ? (value as string[]) : [];
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return selectedIds
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: pool.id, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, pool]);

  const handleChange = useCallback(
    (next: EntitySelection[]) => {
      const ids = next.map((s) => s.id);
      onChange(ids.length > 0 ? ids : null);
    },
    [onChange],
  );

  return (
    <EntitySelector config={config} value={selections} onValueChange={handleChange}>
      <EntitySelectorTrigger
        placeholder="Select project types..."
        size="sm"
        chips
        icon={icon}
        fill={fill}
      />
      <EntitySelectorContent fit>
        <EntitySelectorInput />
        <EntitySelectorPool value={pool.id} />
        <EntitySelectorEmpty>No matching project types</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}
